import { baseCdcEventDataSchema, cdcAuthContextSchema, cdcEventType } from '@tempo-io/common-dto';
import { z } from 'zod';

export const syncStatusEntitySchema = z.object({
  id: z.number(),
  organizationId: z.string(),
  instanceId: z.string(),
  type: z.enum([
    'JIRA_TEAM',
    'TEMPO_TEAM',
    'RESOURCE',
    'AVAILABILITY_SCHEME',
    'TEAM_ROLE',
    'SKILL',
  ]),
  status: z.enum(['PENDING', 'IN_PROGRESS', 'DONE', 'FAILED']),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
});

export const teamEntitySchema = z.object({
  id: z.number(),
  organizationId: z.string(),
  name: z.string(),
  loopId: z.number().nullable(),
});

export const schemeEntitySchema = z.object({
  id: z.number(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
});

export const teamMembershipEntitySchema = z.object({
  id: z.number(),
  teamId: z.number(),
  organizationId: z.string(),
  resourceId: z.number().nullable(),
  openPositionId: z.number().nullable(),
});

export const teamMembershipDeletedSchema = z.object({
  id: z.number(),
  teamId: z.number(),
});

export const deletedEntitySchema = z.object({
  id: z.number(),
});

export const teamIdentifierSchema = deletedEntitySchema;

export const schemeIdentifierSchema = deletedEntitySchema;

const syncStatusDataChangeDTOSchema = z.discriminatedUnion('eventType', [
  baseCdcEventDataSchema.extend({
    entityType: z.literal('sync-status'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: syncStatusEntitySchema,
  }),
  baseCdcEventDataSchema.extend({
    entityType: z.literal('sync-status'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: syncStatusEntitySchema,
  }),
]);

const resourceDataChangeDTOSchema = z.discriminatedUnion('eventType', [
  baseCdcEventDataSchema.extend({
    entityType: z.literal('resource'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  baseCdcEventDataSchema.extend({
    entityType: z.literal('resource'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
  baseCdcEventDataSchema.extend({
    entityType: z.literal('resource'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: z.unknown(),
  }),
]);

const teamMembershipDataChangeDTOSchema = z.discriminatedUnion('eventType', [
  baseCdcEventDataSchema.extend({
    entityType: z.literal('team-membership'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: teamMembershipEntitySchema,
  }),
  baseCdcEventDataSchema.extend({
    entityType: z.literal('team-membership'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: teamMembershipEntitySchema,
  }),
  baseCdcEventDataSchema.extend({
    entityType: z.literal('team-membership'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: teamMembershipDeletedSchema,
  }),
]);

const teamDataChangeDTOSchema = z.discriminatedUnion('eventType', [
  baseCdcEventDataSchema.extend({
    entityType: z.literal('team'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  baseCdcEventDataSchema.extend({
    entityType: z.literal('team'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: teamEntitySchema,
  }),
  baseCdcEventDataSchema.extend({
    entityType: z.literal('team'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: teamIdentifierSchema,
  }),
]);

const holidaySchemeDataChangeDTOSchema = z.discriminatedUnion('eventType', [
  baseCdcEventDataSchema.extend({
    entityType: z.literal('holiday-scheme'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  baseCdcEventDataSchema.extend({
    entityType: z.literal('holiday-scheme'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: schemeEntitySchema,
  }),
  baseCdcEventDataSchema.extend({
    entityType: z.literal('holiday-scheme'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: schemeIdentifierSchema,
  }),
]);

const workloadSchemeDataChangeDTOSchema = z.discriminatedUnion('eventType', [
  baseCdcEventDataSchema.extend({
    entityType: z.literal('workload-scheme'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  baseCdcEventDataSchema.extend({
    entityType: z.literal('workload-scheme'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: schemeEntitySchema,
  }),
  baseCdcEventDataSchema.extend({
    entityType: z.literal('workload-scheme'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: schemeIdentifierSchema,
  }),
]);

const schemeMembershipDataChangeDTOSchema = z.discriminatedUnion('eventType', [
  baseCdcEventDataSchema.extend({
    entityType: z.literal('scheme-membership'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: z.unknown(),
  }),
  baseCdcEventDataSchema.extend({
    entityType: z.literal('scheme-membership'),
    eventType: z.literal(cdcEventType.enum.updated_full),
    entity: z.unknown(),
  }),
]);

export const capacityDataChangeDTOSchema = z.object({
  data: baseCdcEventDataSchema.and(
    z.discriminatedUnion('entityType', [
      syncStatusDataChangeDTOSchema,
      resourceDataChangeDTOSchema,
      teamMembershipDataChangeDTOSchema,
      teamDataChangeDTOSchema,
      holidaySchemeDataChangeDTOSchema,
      workloadSchemeDataChangeDTOSchema,
      schemeMembershipDataChangeDTOSchema,
    ]),
  ),
  authContext: cdcAuthContextSchema,
  metadata: z.record(z.string(), z.string()).optional(),
});

export type CapacityDataChangeDTO = z.infer<typeof capacityDataChangeDTOSchema>;
export type SyncStatusEntity = z.infer<typeof syncStatusEntitySchema>;
export type TeamEntity = z.infer<typeof teamEntitySchema>;
export type SchemeEntity = z.infer<typeof schemeEntitySchema>;
export type TeamMembershipEntity = z.infer<typeof teamMembershipEntitySchema>;
export type TeamMembershipDeleted = z.infer<typeof teamMembershipDeletedSchema>;
export type DeletedEntity = z.infer<typeof deletedEntitySchema>;
export type TeamIdentifier = z.infer<typeof teamIdentifierSchema>;
export type SchemeIdentifier = z.infer<typeof schemeIdentifierSchema>;
